import { ArrowLeft, Printer, Send, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getBillingsDetails } from "@/services/transactionsApi";
import { Button } from "@/components/ui/button";

export interface InvoiceUser {
  _id: string;
  f_name: string;
  l_name: string;
  email: string;
  address: string;
  city: string;
  country: string;
  pincode: string;
}

export interface InvoicePlan {
  _id: string;
  name: string;
  pricing: number;
  duration: string;
}

export interface InvoiceSubscription {
  _id: string;
  plan_id: InvoicePlan;
  start_date: string;
  end_date: string;
  status: number;
  plan_details: InvoicePlan;
}

export interface InvoiceDetails {
  _id: string;
  user_id: InvoiceUser;
  subscription_id: InvoiceSubscription;
  payment_id: string;
  payment_status: number;
  sub_total: string;
  discount_price: string;
  total: string;
  date: string;
  time: string;
  created_at: string;
}

const Invoice = () => {
  const { billing_id } = useParams();
  const [invoice, setInvoice] = useState<InvoiceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoice();
  }, []);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const data = await getBillingsDetails(billing_id!);
      setInvoice(data);
    } catch (error) {
      console.error("Failed to load invoice", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    // return <div className="p-5 text-center">Loading invoice...</div>;
    return (
      <tr>
        <td colSpan={6} className="text-center py-5">
          <div className="spin-container d-flex justify-content-center">
            <div className="atbd-spin-dots spin-sm">
              <span className="spin-dot badge-dot dot-primary"></span>
              <span className="spin-dot badge-dot dot-primary"></span>
              <span className="spin-dot badge-dot dot-primary"></span>
              <span className="spin-dot badge-dot dot-primary"></span>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  if (!invoice) {
    return <div className="p-5 text-center text-danger">Invoice not found</div>;
  }

  // ✅ Extract values
  const user = invoice.user_id;
  const plan = invoice.subscription_id?.plan_details;

  const handleSendInvoice = () => {
    if (!invoice || !invoice.user_id) return;

    const emailTo = invoice.user_id.email ?? "";
    const subject = `Your Invoice #${invoice._id}`;

    const body = `
Hello ${invoice.user_id.f_name},

Please find your invoice details below:

Invoice Number: ${invoice._id}
Plan: ${plan?.name}
Total Amount: ₹${invoice.total}
Date: ${new Date(invoice.date).toLocaleDateString()}

Thank you,
Packtivate Team
  `.trim();

    window.location.href = `mailto:${emailTo}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  const downloadPDF = async () => {
    const invoiceElement = document.getElementById("invoice-print-area");
    const actionButtons = document.querySelector(
      ".invoice-actions",
    ) as HTMLElement;

    if (!invoiceElement) return;

    // Hide buttons
    if (actionButtons) actionButtons.style.display = "none";

    // Remove padding only for PDF
    const originalPadding = invoiceElement.style.padding;
    invoiceElement.style.padding = "0";

    // Apply A4 scaling class
    invoiceElement.classList.add("pdf-scale");

    // Allow DOM update
    await new Promise((r) => setTimeout(r, 300));

    const canvas = await html2canvas(invoiceElement, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

    pdf.save(`invoice_${invoice._id}.pdf`);

    // Restore web UI exactly as before
    invoiceElement.classList.remove("pdf-scale");
    invoiceElement.style.padding = originalPadding;

    if (actionButtons) actionButtons.style.display = "flex";
  };

  const backPath = "/billing";
  return (
    <div className=" " style={{ height: "100vh", overflowY: "auto" }}>
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="shop-breadcrumb">
              <div className="breadcrumb-main">
                <div className=" d-flex align-center">
                  <Button
                    size="sm"
                    variant="shadow"
                    onClick={() => navigate(backPath)}
                    className="gap-2 mx-2 btn-icon btn-info"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {/* Back */}
                  </Button>

                  <h4 className="text-capitalize breadcrumb-title">invoice</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Body */}
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div
              className="payment-invoice global-shadow border bg-white radius-xl w-100 mb-30 invoice-a4"
              id="invoice-print-area"
            >
              <div className="payment-invoice__body">
                <div className="payment-invoice-address d-flex justify-content-sm-between">
                  <div className="payment-invoice-logo">
                    <a href="#">
                      <img
                        className="svg dark"
                        style={{ maxWidth: "195px" }}
                        src="/images/logo.png"
                        alt=""
                      />
                    </a>
                  </div>

                  <div className="payment-invoice-address__area">
                    <address>
                      Packtivate
                      <br />
                      Ratna Umed Residency
                      <br />
                      Louis Wadi, Thane 400604, India
                      <br />
                      Reg. number : 245000003513
                    </address>
                  </div>
                </div>

                <div className="payment-invoice-qr d-flex justify-content-between mb-40 px-xl-50 px-30 py-sm-30 py-20">
                  <div className="d-flex justify-content-center mb-lg-0 mb-25">
                    <div className="payment-invoice-qr__number">
                      <div className="display-3">Invoice</div>
                      <p>
                        Invoice id  : <span>{invoice._id}</span>
                      </p>
                      <p>Payment id: <span>{invoice.payment_id}</span></p>
                      <p>
                        Date :
                        <span>
                          {new Date(invoice.date).toLocaleDateString()}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="d-flex justify-content-center">
                    <div className="payment-invoice-qr__address">
                      <p>Invoice To:</p>
                      <span>
                        {user.f_name} {user.l_name}
                      </span>
                      <address>
                        {user.address}
                        <br />
                        {user.city},{user.pincode}
                      </address>
                      <br />
                    </div>
                  </div>
                </div>

                <div className="payment-invoice-table">
                  <div className="table-responsive">
                    <table id="cart" className="table table-borderless">
                      <thead>
                        <tr className="product-cart__header">
                          <th>#</th>
                          <th>Plans</th>
                          <th className="text-right">Price Per Unit</th>
                          <th className="text-right">Quantity</th>
                          <th className="text-right">Order Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr>
                          <th>1</th>
                          <td className="Product-cart-title">
                            <div className="media align-items-center">
                              <div className="media-body">
                                <h5 className="mt-0">{plan?.name}</h5>
                              </div>
                            </div>
                          </td>
                          <td className="unit text-right">₹{plan?.pricing}</td>
                          <td className="invoice-quantity text-right">1</td>
                          <td className="text-right order">
                            ₹{invoice.sub_total}
                          </td>
                        </tr>
                      </tbody>

                      <tfoot>
                        <tr>
                          <td colSpan={3}></td>

                          <td className="order-summery float-right">
                            <div className="total">
                              <div className="subtotalTotal mb-0 text-right">
                                Subtotal :
                              </div>
                              <div className="taxes mb-0 text-right">
                                discount :
                              </div>
                            </div>

                            <div className="total-money d-flex justify-content-between align-items-center mt-2 text-right float-right">
                              <h6>Total :</h6>
                            </div>
                          </td>

                          <td>
                            <div className="total-order float-right text-right fs-14 fw-500">
                              <p>₹{invoice.sub_total}</p>
                              <p>-₹{invoice.discount_price}</p>
                              <h5 className="text-primary">₹{invoice.total}</h5>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="payment-invoice__btn mt-lg-50 pt-lg-30 mt-30 pt-20 no-print invoice-actions">
                    <button
                      type="button"
                      className="btn border rounded-pill bg-normal text-gray px-25 print-btn"
                      onClick={() => window.print()}
                    >
                      <Printer size={16} className="mr-2" />
                      Print
                    </button>

                    <button
                      type="button"
                      className="btn-primary btn rounded-pill px-25 text-white download"
                      onClick={downloadPDF}
                    >
                      <Upload size={16} className="mr-2" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
